import { z } from 'zod';
import { OrgRole } from './auth.js';
import { PROJECT_KEY_MAX } from './project.js';
import { IanaTimezone } from './timezone.js';
/**
 * Gộp / tách tổ chức — APM-ORGMS-c, docs/plan-org-restructure.md §3.
 *
 * Task này chỉ có **gộp** (B nhập vào A): đề xuất, link + lời mời email, cấu hình có
 * phiên bản, vòng duyệt. Tách (`OrgSplitConfig`) là `APM-ORGMS-g`.
 */
/** Đề xuất quá hạn này mà chưa được duyệt đủ ⇒ `expired` (§3.2). Hằng số, không biến env (L8). */
export const ORG_RESTRUCTURE_TTL_DAYS = 14;
export const OrgRestructureKind = z.enum(['merge', 'split']);
export const OrgRestructureStatus = z.enum([
    'awaiting_partner',
    'awaiting_approval',
    'approved',
    'executing',
    'done',
    'cancelled',
    'expired',
    'failed',
]);
/**
 * "Đang mở" — một tổ chức là bên của một đề xuất ở các trạng thái này thì không tạo / nhận
 * đề xuất khác (`ORG_RESTRUCTURE_IN_PROGRESS`). Hai lượt gộp chồng nhau là hai bộ ánh xạ mã
 * dự án cãi nhau.
 *
 * `failed` cũng mở (APM-ORGMS-e): lượt chạy dừng giữa chừng để lại dữ liệu nửa chuyển, và chỉ
 * có hai đường ra — SUPER_ADMIN "Chạy tiếp", hoặc (khi chưa chuyển gì, `execution.frozen =
 * false`) Admin huỷ. Một đề xuất mới chồng lên đó là hai lượt cãi nhau về cùng một tổ chức.
 */
export const OPEN_ORG_RESTRUCTURE_STATUSES = [
    'awaiting_partner',
    'awaiting_approval',
    'approved',
    'executing',
    'failed',
];
/** Còn sửa cấu hình / duyệt / từ chối được. */
export const PENDING_ORG_RESTRUCTURE_STATUSES = [
    'awaiting_partner',
    'awaiting_approval',
];
/**
 * Chiều gộp, nhìn từ tổ chức **khởi xướng** (§3.8 bước 1):
 * - `into_initiator` — "Nhập tổ chức kia **vào đây**": khởi xướng là A (giữ lại).
 * - `into_partner` — "Nhập tổ chức này **vào tổ chức kia**": khởi xướng là B (bị đóng).
 */
export const OrgMergeDirection = z.enum(['into_initiator', 'into_partner']);
const MAX_ROLE_HOLDERS = 50;
/** Trần số dự án đổi mã trong một lượt — cùng trần danh sách của báo cáo (L10). */
const MAX_KEY_RENAMES = 500;
const ProjectKeyRename = z.object({
    projectId: z.uuid(),
    key: z
        .string()
        .trim()
        .min(2)
        .max(PROJECT_KEY_MAX, `Mã dự án tối đa ${PROJECT_KEY_MAX} ký tự`)
        .regex(/^[A-Z][A-Z0-9]*$/, 'Mã dự án chỉ gồm chữ HOA và số, bắt đầu bằng chữ'),
});
/**
 * Cấu hình một lượt gộp — thứ **mọi** người duyệt ký lên (§3.3). Sửa ⇒ `configVersion + 1`
 * ⇒ mọi lượt duyệt cũ hết giá trị.
 *
 * - `admins` / `directors`: Admin và Director của tổ chức **sau gộp**, chọn trong thành viên
 *   của cả hai bên (≥ 1 Admin). Người có tên được bật cờ / vai lúc chạy (§3.6 bước 7).
 * - `projectKeyRenames`: mã mới cho dự án trùng mã giữa hai bên (preflight chỉ ra ở ORGMS-d).
 */
export const OrgMergeConfig = z
    .object({
    direction: OrgMergeDirection,
    admins: z.array(z.uuid()).min(1, 'Cần ít nhất một Admin').max(MAX_ROLE_HOLDERS),
    directors: z.array(z.uuid()).max(MAX_ROLE_HOLDERS),
    projectKeyRenames: z.array(ProjectKeyRename).max(MAX_KEY_RENAMES),
})
    .superRefine((config, ctx) => {
    const dup = (values) => values.find((value, index) => values.indexOf(value) !== index);
    if (dup(config.admins))
        ctx.addIssue({ code: 'custom', path: ['admins'], message: 'Trùng người' });
    if (dup(config.directors))
        ctx.addIssue({ code: 'custom', path: ['directors'], message: 'Trùng người' });
    if (dup(config.projectKeyRenames.map((row) => row.projectId)))
        ctx.addIssue({ code: 'custom', path: ['projectKeyRenames'], message: 'Trùng dự án' });
    if (dup(config.projectKeyRenames.map((row) => row.key)))
        ctx.addIssue({ code: 'custom', path: ['projectKeyRenames'], message: 'Trùng mã mới' });
});
/** Tạo đề xuất gộp. Bỏ trống `admins` ⇒ người tạo là Admin duy nhất của cấu hình ban đầu. */
export const CreateOrgMergeInput = z.object({
    direction: OrgMergeDirection,
    admins: z.array(z.uuid()).min(1).max(MAX_ROLE_HOLDERS).optional(),
    directors: z.array(z.uuid()).max(MAX_ROLE_HOLDERS).optional(),
});
/** Sửa cấu hình — đòi `configVersion` hiện hành; lệch ⇒ `ORG_RESTRUCTURE_STALE_VERSION`. */
export const UpdateOrgRestructureConfigInput = z.object({
    configVersion: z.number().int().positive(),
    config: OrgMergeConfig,
});
/** Duyệt **đúng bản** mình đang xem. */
export const ApproveOrgRestructureInput = z.object({
    configVersion: z.number().int().positive(),
});
export const RejectOrgRestructureInput = z.object({
    reason: z.string().trim().min(1, 'Hãy ghi lý do').max(2_000),
});
/**
 * Mời Admin bên kia bằng email (§1.2). Trả `202` y hệt nhau dù email có tài khoản hay không
 * (L9) — nên `.trim()` / `.toLowerCase()` đứng **trước** kiểm dạng email.
 */
export const InviteOrgRestructurePartnerInput = z.object({
    emails: z.array(z.string().trim().toLowerCase().max(320).pipe(z.email())).min(1).max(20),
});
/**
 * Nhận link. `orgId` là tổ chức mình là Admin sẽ làm bên kia; bỏ trống thì hệ thống chọn khi
 * chỉ có đúng một tổ chức hợp lệ.
 */
export const AcceptOrgRestructureInviteInput = z.object({
    token: z.string().trim().min(16).max(200),
    orgId: z.uuid().optional(),
});
export const OrgRestructureOrgRef = z.object({ id: z.uuid(), name: z.string() });
/**
 * Một người duyệt — tính **lúc đọc** (§3.3): mọi thành viên còn hoạt động có
 * `org_restructure:approve` ở một bên. Người đã rời vai mà còn hàng quyết định thì không nằm
 * đây nữa — tập người duyệt là của hôm nay.
 */
export const OrgRestructureApprover = z.object({
    userId: z.uuid(),
    displayName: z.string(),
    /** Bên người này duyệt thay. Là thành viên duyệt được ở cả hai bên ⇒ bên giữ lại. */
    orgId: z.uuid(),
    isOrgAdmin: z.boolean(),
    orgRole: OrgRole,
    decision: z.enum(['approve', 'reject']).nullable(),
    /** Bản cấu hình của quyết định gần nhất. Khác `configVersion` hiện hành = "duyệt bản cũ". */
    decidedVersion: z.number().int().nullable(),
    decidedAt: z.iso.datetime().nullable(),
    reason: z.string().nullable(),
    /** `decision = approve` **và** `decidedVersion = configVersion`. */
    approvedCurrent: z.boolean(),
});
// ── Chạy gộp — APM-ORGMS-e ─────────────────────────────────────────────────
/**
 * Các bước của job chạy gộp, theo thứ tự (§3.6). Mỗi bước là một hoặc nhiều transaction; bước
 * đã xong ghi vào `org_restructure.progress` **trong** transaction của nó, nên lần chạy lại
 * (job chết giữa chừng, SUPER_ADMIN "Chạy tiếp") bắt đầu từ bước sau cùng đã commit.
 */
export const OrgMergeStep = z.enum([
    /** Đóng băng hai tổ chức: `executing`, mọi request vào hai bên ⇒ 423. */
    'freeze',
    /** Preflight lại — lỗi chặn mới ⇒ `failed`, mở băng. Chốt mã dự án cuối. */
    'preflight',
    /** Danh mục (loại C): đổi tên mục trùng slug, đổi `org_id`. */
    'catalogs',
    /** Từng dự án một transaction: chốt giá bậc, đổi mã, đổi `org_id` mọi hàng loại P. */
    'projects',
    /** Phần còn lại mang `org_id` của B (loại U, hàng không dự án…), theo lô; xung đột ở lại B. */
    'personal',
    /** Tư cách thành viên + tổ chức gốc + vai theo `admins` / `directors`. */
    'members',
    /** Thu hồi phiên, lời mời, job đang chờ của B. */
    'infra',
    /** B xoá mềm, `org_lineage`, báo cáo, `done`, mở băng. */
    'finish',
]);
/** Trần mỗi danh sách của báo cáo sau chạy — L10: JSON trong DB, không phụ thuộc S3. */
export const ORG_RESTRUCTURE_REPORT_LIST_MAX = 500;
const count = z.number().int().nonnegative();
/** Tiến độ của lượt chạy — đọc từ `org_restructure.progress`. `null` khi chưa chạy. */
export const OrgRestructureExecution = z.object({
    /** Hai tổ chức đang đóng băng (423). `failed` mà vẫn `true` = dữ liệu nửa chuyển. */
    frozen: z.boolean(),
    frozenAt: z.iso.datetime().nullable(),
    doneSteps: z.array(OrgMergeStep),
    /** Bước kế tiếp sẽ chạy; `null` khi đã xong. */
    currentStep: OrgMergeStep.nullable(),
    projects: z.object({ moved: count, total: count }),
});
const reportList = (item) => z.object({ total: count, items: z.array(item) });
/**
 * Một cột đã đổi sang hàng khác trong lượt chạy. `events` = số event đi kèm (0 với bảng dẫn xuất /
 * cột không là state của aggregate nào — vd. `provider_id` / `host_id`, APM-ORGINT-f).
 */
const remapRow = z.object({ model: z.string(), column: z.string(), rows: count, events: count });
/**
 * Báo cáo sau chạy (§3.6 bước 9, L10) — thứ đã đổi tên, đã chốt, đã bỏ lại. Mỗi danh sách
 * dừng ở `ORG_RESTRUCTURE_REPORT_LIST_MAX` kèm tổng thật.
 */
export const OrgMergeReport = z.object({
    projectKeys: reportList(z.object({ projectId: z.uuid(), from: z.string(), to: z.string() })),
    catalogRenames: reportList(z.object({
        catalog: z.string(),
        id: z.uuid(),
        slug: z.string(),
        newSlug: z.string(),
        name: z.string(),
        newName: z.string(),
    })),
    /** Giá bậc của B chốt thành `project_member_rate` tới ngày gộp (§1.2). */
    levelRateLocks: z.object({ members: count, rows: count }),
    members: z.object({
        /** Người chỉ ở B — tư cách thành viên chuyển sang A. */
        joined: count,
        /** Người ở cả hai — giữ hàng của A, hàng B xoá mềm. */
        merged: count,
        roleChanges: count,
    }),
    /** Hàng mang `org_id` của B **không** chuyển vì đụng dữ liệu của A — của A thắng. */
    kept: z.array(z.object({ model: z.string(), rows: count })),
    /** Cấu hình của B không chuyển (loại S) — của A thắng. */
    dropped: z.array(z.object({ model: z.string(), rows: count })),
    /** Cột cấu hình `organization` mà B khác A. */
    orgSettings: z.array(z.string()),
    folderMerges: count,
    figmaRevoked: count,
    /**
     * Máy chủ git của B trùng địa chỉ với máy chủ của A ⇒ đã gộp vào máy chủ của A, hàng của B bị
     * xoá (APM-ORGINT-f). Tuỳ chọn: báo cáo trước `-f` không có.
     */
    gitHostsMerged: count.optional(),
    /** Cột trỏ máy chủ git đã đổi sang máy chủ của A — không event (plan-org-integrations D2). */
    remapped: z.array(remapRow).optional(),
    revoked: z.object({
        refreshTokens: count,
        orgInvites: count,
        projectInvites: count,
        jobs: count,
    }),
    /** Lỗi chặn khi preflight lại ở bước 2 (`failed`, không chuyển gì). */
    blockers: z.array(z.object({ code: z.string(), message: z.string(), total: count })),
});
export const OrgRestructureView = z.object({
    id: z.uuid(),
    kind: OrgRestructureKind,
    status: OrgRestructureStatus,
    /** Tổ chức khởi xướng. */
    initiatorOrg: OrgRestructureOrgRef,
    /** Gộp: B (nhập vào). `null` khi B là bên chưa nhận link. */
    sourceOrg: OrgRestructureOrgRef.nullable(),
    /** Gộp: A (giữ lại). `null` khi A là bên chưa nhận link. */
    targetOrg: OrgRestructureOrgRef.nullable(),
    initiatedBy: z.object({ id: z.uuid(), displayName: z.string() }),
    config: OrgMergeConfig,
    configVersion: z.number().int().positive(),
    expiresAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    executedAt: z.iso.datetime().nullable(),
    errorCode: z.string().nullable(),
    approvers: z.array(OrgRestructureApprover),
    approverCount: z.number().int().nonnegative(),
    approvedCount: z.number().int().nonnegative(),
    /** Tiến độ chạy (APM-ORGMS-e). `null` trước khi chạy. */
    execution: OrgRestructureExecution.nullable(),
    /** Báo cáo sau chạy / lỗi chặn khi preflight lại (APM-ORGMS-e). */
    report: OrgMergeReport.nullable(),
    /**
     * Tách (APM-ORGMS-g): cấu hình thật của lượt tách; `null` với gộp. Với tách, `config` ở trên
     * chỉ là **hình chiếu** để giao diện gộp đọc chung Admin / Director (`admins`, `directors`;
     * `direction = into_initiator`, `projectKeyRenames` rỗng) — sửa cấu hình tách đi qua
     * `PUT …/:id/split-config`, không qua `config`.
     */
    split: z.lazy(() => OrgSplitConfig).nullable(),
    /** Tách: báo cáo sau chạy / lỗi chặn khi preflight lại. `null` với gộp (đọc `report`). */
    splitReport: z.lazy(() => OrgSplitReport).nullable(),
    /** Người đang xem làm được gì — nút ở giao diện đọc từ đây, không tự suy vai. */
    viewer: z.object({
        isApprover: z.boolean(),
        canEditConfig: z.boolean(),
        canCancel: z.boolean(),
    }),
});
/** Kết quả tạo: token chỉ đi ra ở đây và ở `…/link`, không bao giờ nằm trong view. */
export const CreatedOrgRestructure = z.object({
    restructure: OrgRestructureView,
    token: z.string(),
});
/** Đề xuất đang mở của tổ chức trong phiên — bọc lại, không trả `null` trần. */
export const CurrentOrgRestructure = z.object({ restructure: OrgRestructureView.nullable() });
/** Chép lại link. `token: null` khi link không còn dùng được (đã có người nhận, hết hạn, đóng). */
export const OrgRestructureLink = z.object({
    token: z.string().nullable(),
    expiresAt: z.iso.datetime(),
});
/** Câu trả lời của lời mời email — **một** hình dạng cho mọi trường hợp (L9). */
export const OrgRestructureInviteSent = z.object({ status: z.literal('queued') });
/**
 * Xem trước link (§3.7 trang nhận link): hồ sơ tổ chức mời — **không** danh sách người.
 * `eligibleOrgs` là tổ chức **của chính người xem** mà họ là Admin và nhận được đề xuất.
 */
export const OrgRestructureInvitePreview = z.object({
    kind: OrgRestructureKind,
    direction: OrgMergeDirection,
    expiresAt: z.iso.datetime(),
    org: z.object({
        name: z.string(),
        description: z.string().nullable(),
        logoUrl: z.string().nullable(),
        website: z.string().nullable(),
        industry: z.string().nullable(),
        memberCount: z.number().int().nonnegative(),
        projectCount: z.number().int().nonnegative(),
    }),
    inviter: z.object({ name: z.string() }).nullable(),
    eligibleOrgs: z.array(OrgRestructureOrgRef),
});
/** `not_admin`: người nhận không là Admin tổ chức nào nhận được — một kết cục, không phải lỗi. */
export const AcceptOrgRestructureResult = z.discriminatedUnion('outcome', [
    z.object({ outcome: z.literal('joined'), restructure: OrgRestructureView }),
    z.object({ outcome: z.literal('not_admin') }),
]);
// ── Preflight — APM-ORGMS-d ─────────────────────────────────────────────────
/** Trần mỗi danh sách của preflight — chạy trong request nên đếm đủ, liệt kê có trần. */
export const ORG_RESTRUCTURE_PREFLIGHT_LIST_MAX = 50;
/**
 * Lỗi chặn — có một cái là lượt duyệt cuối không sang `approved` (`ORG_RESTRUCTURE_BLOCKED`,
 * `details.blockers`), và job chạy (ORGMS-e) dừng ở bước preflight lại.
 */
export const OrgRestructureBlockerCode = z.enum([
    /** Chưa có bên kia — chưa có gì để kiểm. */
    'PARTNER_MISSING',
    /** Một bên là tổ chức demo (L7). */
    'ORG_DEMO',
    /** Một bên là không gian cá nhân — cùng lý lẽ với demo: không phải khách hàng thật (APM-PSPACE-a). */
    'ORG_PERSONAL',
    /** Một bên đã bị xoá. */
    'ORG_DELETED',
    /** Một bên đang là bên của một lượt gộp / tách **khác** đang chạy. */
    'ORG_EXECUTING',
    /** Hai tài khoản khác nhau cùng email, một ở A một ở B (L12) — đợt này không tự gộp tài khoản. */
    'DUPLICATE_EMAIL',
    /** Không còn Admin nào trong cấu hình là thành viên đang hoạt động. */
    'NO_ADMIN',
    /** Người trong `admins` / `directors` không còn là thành viên đang hoạt động của một bên. */
    'ROLE_HOLDER_NOT_MEMBER',
    /** Mã dự án sau khi đổi vẫn trùng (với A, hoặc giữa hai dự án của B). */
    'PROJECT_KEY_CONFLICT',
    /** `projectKeyRenames` nhắm dự án không thuộc tổ chức nhập vào (vd. sau khi đổi chiều gộp). */
    'PROJECT_KEY_RENAME_STALE',
]);
/** Cảnh báo — không chặn, người duyệt đọc trước khi ký. */
export const OrgRestructureWarningCode = z.enum([
    /** Tệp của B + A vượt hạn mức của A (L11): tệp cũ đọc được, tải lên mới bị chặn. */
    'STORAGE_QUOTA',
    /** Thành viên dự án B đang ăn giá bậc của B ⇒ lúc chạy chốt thành `project_member_rate` (§1.2). */
    'LEVEL_RATE_LOCK',
    /** Link chia sẻ `audience = org` của B từ nay mở cho mọi thành viên tổ chức giữ lại. */
    'ORG_AUDIENCE_SHARE_LINKS',
    /** Cả hai có kết nối Figma đang dùng ⇒ kết nối của B sang A ở trạng thái đã thu hồi. */
    'FIGMA_CONNECTION_REVOKED',
    /**
     * Máy chủ git của B trùng địa chỉ web với máy chủ của A (APM-ORGINT-f) ⇒ kết nối / kho /
     * tài khoản git của B trỏ sang máy chủ của A, máy chủ của B bị xoá.
     */
    'GIT_HOSTS_MERGED',
]);
/** Một mục trong danh sách của lỗi chặn / cảnh báo. `id` là id của hàng khi có. */
export const OrgRestructurePreflightItem = z.object({
    id: z.uuid().nullable(),
    label: z.string(),
});
const issue = (code) => z.object({
    code,
    /** Câu tiếng Việt dự phòng — giao diện dịch theo `code`. */
    message: z.string(),
    /** Số mục thật; `items` dừng ở `ORG_RESTRUCTURE_PREFLIGHT_LIST_MAX`. */
    total: z.number().int().nonnegative(),
    items: z.array(OrgRestructurePreflightItem),
});
export const OrgRestructureBlocker = issue(OrgRestructureBlockerCode);
export const OrgRestructureWarning = issue(OrgRestructureWarningCode);
const capped = (item) => z.object({ total: z.number().int().nonnegative(), items: z.array(item) });
/** Dự án của B đổi mã — trùng với A (`default` = `KEY2`, `KEY3`…) hoặc có tên trong cấu hình. */
export const OrgRestructureProjectKeyChange = z.object({
    projectId: z.uuid(),
    name: z.string(),
    key: z.string(),
    proposedKey: z.string(),
    /** `config` = lấy từ `projectKeyRenames`; `default` = hậu tố số tự chọn. */
    source: z.enum(['config', 'default']),
    /** Mã gốc có trùng với một dự án của A không (đổi mã không trùng là admin tự muốn). */
    conflicts: z.boolean(),
});
/** Danh mục có slug trùng — §3.5 loại C: đổi tên, **không** gộp mục (§1.3). */
export const OrgRestructureCatalog = z.enum([
    'department',
    'tech_stack',
    'task_type',
    'wbs_template',
    'document_template',
]);
export const OrgRestructureCatalogRename = z.object({
    catalog: OrgRestructureCatalog,
    id: z.uuid(),
    slug: z.string(),
    name: z.string(),
    newSlug: z.string(),
    newName: z.string(),
});
export const OrgRestructurePersonInBoth = z.object({
    userId: z.uuid(),
    displayName: z.string(),
    email: z.string(),
});
/**
 * Dữ liệu theo người (loại U) của người có ở cả hai mà đụng ràng buộc duy nhất của A.
 * `report` — của A thắng, của B vào báo cáo; `merge_folder` — dồn tệp vào thư mục cùng tên
 * của A; `list` — giữ cả hai, chỉ liệt kê.
 */
export const OrgRestructurePersonalConflict = z.object({
    model: z.string(),
    rows: z.number().int().nonnegative(),
    handling: z.enum(['report', 'merge_folder', 'list']),
});
/** Cấu hình của B **không** chuyển (loại S, `drop_to_report`) — của A thắng. */
export const OrgRestructureDroppedConfig = z.object({
    model: z.string(),
    rows: z.number().int().nonnegative(),
});
/**
 * Preflight một lượt gộp — `GET org-restructures/:id/preflight`. Chỉ đọc; chạy lại ở bước 2
 * của job (dữ liệu đổi từ lúc duyệt). Bên chưa đủ ⇒ mọi số là 0 và một lỗi `PARTNER_MISSING`.
 */
export const OrgRestructurePreflight = z.object({
    restructureId: z.uuid(),
    configVersion: z.number().int().positive(),
    computedAt: z.iso.datetime(),
    /** B — nhập vào, bị đóng. */
    sourceOrg: OrgRestructureOrgRef.nullable(),
    /** A — giữ lại. */
    targetOrg: OrgRestructureOrgRef.nullable(),
    moving: z.object({
        /** Dự án chưa xoá của B. */
        projects: z.number().int().nonnegative(),
        /** Thành viên chưa rời của B. */
        members: z.number().int().nonnegative(),
        /** Trong đó chưa là thành viên của A. */
        newMembers: z.number().int().nonnegative(),
        /** Tệp đang tính hạn mức của B (`pending` + `committed`). */
        files: z.number().int().nonnegative(),
        fileBytes: z.number().int().nonnegative(),
    }),
    storage: z.object({
        targetUsedBytes: z.number().int().nonnegative(),
        targetQuotaBytes: z.number().int().nonnegative(),
        /** `targetUsedBytes + moving.fileBytes`. */
        afterBytes: z.number().int().nonnegative(),
    }),
    projectKeys: capped(OrgRestructureProjectKeyChange),
    catalogRenames: capped(OrgRestructureCatalogRename),
    peopleInBoth: capped(OrgRestructurePersonInBoth),
    personalConflicts: z.array(OrgRestructurePersonalConflict),
    droppedConfig: z.object({
        models: z.array(OrgRestructureDroppedConfig),
        /** Cột cấu hình của `organization` mà B khác A (múi giờ, OKR, hạn mức…) — theo A. */
        orgSettings: z.array(z.string()),
    }),
    /** Thành viên dự án B đang ăn giá bậc của B — sẽ được chốt thành giá riêng tới ngày gộp. */
    levelRateLock: z.object({
        members: z.number().int().nonnegative(),
        projects: z.number().int().nonnegative(),
    }),
    warnings: z.array(OrgRestructureWarning),
    blockers: z.array(OrgRestructureBlocker),
});
// ── Tách — APM-ORGMS-g ──────────────────────────────────────────────────────
/**
 * Một người được chọn sang tổ chức mới C (§1.2): `move` — chuyển hẳn (rời A, dữ liệu cá nhân đi
 * theo, `user.org_id` sang C nếu gốc là A); `both` — ở cả hai (tư cách mới ở C, mọi thứ ở A giữ
 * nguyên, đơn giá riêng `cost_rate` được **chép** sang C).
 */
export const OrgSplitPersonMode = z.enum(['move', 'both']);
export const OrgSplitPerson = z.object({ userId: z.uuid(), mode: OrgSplitPersonMode });
/** `organization.slug` là `VARCHAR(64)`, duy nhất toàn hệ thống. */
export const ORG_SLUG_MAX = 64;
const MAX_SPLIT_PEOPLE = 2_000;
const MAX_SPLIT_PROJECTS = 200;
/** Slug tổ chức mới: chữ thường, số, gạch nối — `.trim()` / `.toLowerCase()` đứng trước kiểm dạng. */
export const OrgSplitSlug = z
    .string()
    .trim()
    .toLowerCase()
    .min(2)
    .max(ORG_SLUG_MAX)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug chỉ gồm chữ thường, số và dấu gạch nối');
const splitShape = {
    name: z.string().trim().min(1, 'Hãy đặt tên tổ chức mới').max(200),
    slug: OrgSplitSlug,
    /** Mặc định múi giờ của A (điền lúc tạo đề xuất). */
    timezone: IanaTimezone,
    /** Dự án chuyển hẳn sang C. */
    projectIds: z.array(z.uuid()).max(MAX_SPLIT_PROJECTS),
    people: z.array(OrgSplitPerson).min(1, 'Hãy chọn ít nhất một người').max(MAX_SPLIT_PEOPLE),
    /** Admin của C — thuộc `people`, ≥ 1. */
    admins: z.array(z.uuid()).min(1, 'Cần ít nhất một Admin').max(MAX_ROLE_HOLDERS),
    /** Director của C — thuộc `people`. */
    directors: z.array(z.uuid()).max(MAX_ROLE_HOLDERS),
};
function refineSplit(config, ctx) {
    const dup = (values) => values.find((value, index) => values.indexOf(value) !== index);
    if (dup(config.projectIds))
        ctx.addIssue({ code: 'custom', path: ['projectIds'], message: 'Trùng dự án' });
    if (dup(config.people.map((p) => p.userId)))
        ctx.addIssue({ code: 'custom', path: ['people'], message: 'Trùng người' });
    if (dup(config.admins))
        ctx.addIssue({ code: 'custom', path: ['admins'], message: 'Trùng người' });
    if (dup(config.directors))
        ctx.addIssue({ code: 'custom', path: ['directors'], message: 'Trùng người' });
    const chosen = new Set(config.people.map((p) => p.userId));
    if (config.admins.some((id) => !chosen.has(id)))
        ctx.addIssue({
            code: 'custom',
            path: ['admins'],
            message: 'Admin của tổ chức mới phải nằm trong danh sách người được chọn',
        });
    if (config.directors.some((id) => !chosen.has(id)))
        ctx.addIssue({
            code: 'custom',
            path: ['directors'],
            message: 'Director của tổ chức mới phải nằm trong danh sách người được chọn',
        });
}
/**
 * Cấu hình một lượt **tách** (một phần của A thành tổ chức mới C) — §1.2, §3.5. Không có link /
 * email: mọi người duyệt đều đã ở A. Người duyệt = mọi Admin + Director của A **cộng** mọi người
 * có tên trong `admins` / `directors` (§3.3). Sửa ⇒ `configVersion + 1`.
 */
export const OrgSplitConfig = z.object(splitShape).superRefine(refineSplit);
/** Tạo đề xuất tách — `POST org-restructures/splits`. `timezone` bỏ trống ⇒ múi giờ của A. */
export const CreateOrgSplitInput = z
    .object({ ...splitShape, timezone: IanaTimezone.optional() })
    .superRefine(refineSplit);
/** Sửa cấu hình tách — `PUT org-restructures/:id/split-config`, đòi `configVersion` hiện hành. */
export const UpdateOrgSplitConfigInput = z.object({
    configVersion: z.number().int().positive(),
    config: OrgSplitConfig,
});
/** Kết quả tạo đề xuất tách — không có token (không có link). */
export const CreatedOrgSplit = z.object({ restructure: OrgRestructureView });
/** Liên kết vắt qua ranh giới khi tách (§3.5) — preflight liệt kê, lúc chạy cắt (có event). */
export const OrgSplitLinkKind = z.enum([
    'task_dependency',
    'sprint_dependency',
    'raid_task_link',
    'change_request_task',
    'objective_task',
    'bug_task',
    'issue_task',
]);
/** Lỗi chặn của preflight tách — cùng vai với `OrgRestructureBlockerCode` của gộp. */
export const OrgSplitBlockerCode = z.enum([
    /** A là tổ chức demo (L7). */
    'ORG_DEMO',
    /** A là không gian cá nhân (APM-PSPACE-a). */
    'ORG_PERSONAL',
    /** A đã bị xoá. */
    'ORG_DELETED',
    /** A đang là bên của một lượt gộp / tách **khác** đang chạy. */
    'ORG_EXECUTING',
    /** Slug của tổ chức mới đã có tổ chức khác giữ (`uq_org_slug` toàn hệ thống). */
    'SLUG_TAKEN',
    /** Không còn Admin nào của C là thành viên đang hoạt động của A. */
    'NO_ADMIN',
    /** Người được chọn không còn là thành viên đang hoạt động của A. */
    'PERSON_NOT_MEMBER',
    /** Dự án được chọn không còn thuộc A (đã xoá, hoặc không phải của A). */
    'PROJECT_NOT_IN_ORG',
    /** Thành viên của dự án được chọn mà không có trong `people` — bỏ họ khỏi dự án trước (§3.5). */
    'PROJECT_MEMBER_NOT_SELECTED',
    /** Người "chuyển hẳn" là Admin / Director cuối cùng của A. */
    'LAST_ROLE_HOLDER_LEAVING',
]);
export const OrgSplitWarningCode = z.enum([
    /** Liên kết giữa dự án chuyển và dự án ở lại — sẽ bị cắt (có event). */
    'CROSS_LINKS',
    /** Người "chuyển hẳn" đang là quản lý trực tiếp của người ở lại — quản lý ở A về trống. */
    'LINE_MANAGER_RESET',
    /** Dự án chuyển dùng key AI của người không "chuyển hẳn" — C sẽ tiêu tiền trên key của người ở A. */
    'PROJECT_AI_KEY_STAYS',
    /** Dự án chuyển có tệp Figma — kết nối Figma không chép (bí mật của A), Admin C tự cấu hình. */
    'FIGMA_NOT_COPIED',
    /** Tệp của dự án chuyển vượt hạn mức dung lượng của C (chép từ A). */
    'STORAGE_QUOTA',
    /**
     * Nguồn đồng bộ dự án chuyển dùng mang bí mật app (Lark / Meegle) — bản chép ở C dùng chung app
     * của A; Admin C nên tạo app riêng rồi dán đè (APM-ORGINT-f).
     */
    'INTEGRATION_SECRETS_COPIED',
]);
export const OrgSplitBlocker = issue(OrgSplitBlockerCode);
export const OrgSplitWarning = issue(OrgSplitWarningCode);
const rowCount = z.object({ model: z.string(), rows: z.number().int().nonnegative() });
/**
 * Preflight một lượt tách — `GET org-restructures/:id/preflight` trả hình này khi `kind =
 * split` (`kind` là trường phân biệt; bản gộp không có `kind`). Chỉ đọc; chạy lại ở bước 2 của
 * job.
 */
export const OrgSplitPreflight = z.object({
    kind: z.literal('split'),
    restructureId: z.uuid(),
    configVersion: z.number().int().positive(),
    computedAt: z.iso.datetime(),
    /** A — tổ chức gốc. */
    sourceOrg: OrgRestructureOrgRef.nullable(),
    newOrg: z.object({ name: z.string(), slug: z.string(), timezone: z.string() }),
    moving: z.object({
        projects: z.number().int().nonnegative(),
        /** Người "chuyển hẳn" / "ở cả hai". */
        movePeople: z.number().int().nonnegative(),
        bothPeople: z.number().int().nonnegative(),
        /** Tệp của dự án chuyển (`pending` + `committed`). */
        files: z.number().int().nonnegative(),
        fileBytes: z.number().int().nonnegative(),
    }),
    /** Danh mục của A sẽ chép sang C (loại C) — số mục mỗi danh mục. */
    catalogCopies: z.array(rowCount),
    /** Cấu hình của A sẽ chép sang C (loại S) — Figma không chép. */
    configCopies: z.array(rowCount),
    /** Liên kết vắt qua ranh giới — sẽ bị cắt. */
    crossLinks: z.array(z.object({ kind: OrgSplitLinkKind, rows: z.number().int().nonnegative() })),
    warnings: z.array(OrgSplitWarning),
    blockers: z.array(OrgSplitBlocker),
});
/**
 * Báo cáo sau chạy tách (L10) — mỗi danh sách dừng ở `ORG_RESTRUCTURE_REPORT_LIST_MAX` kèm tổng.
 * `remapped`: cột trỏ danh mục đã đổi sang bản chép ở C, `events` = số event đi kèm (0 với bảng
 * dẫn xuất / cột không phải state của aggregate nào).
 */
export const OrgSplitReport = z.object({
    newOrg: OrgRestructureOrgRef.nullable(),
    projects: reportList(z.object({ projectId: z.uuid(), key: z.string() })),
    catalogCopies: z.array(rowCount),
    configCopies: z.array(rowCount),
    remapped: z.array(remapRow),
    links: z.array(z.object({ kind: OrgSplitLinkKind, rows: count })),
    /** Dữ liệu cá nhân (loại U) của người "chuyển hẳn" đã sang C. */
    personal: z.array(rowCount),
    /** Hàng chép sang C cho người "ở cả hai" (`cost_rate`, `user_skill`). */
    copied: z.array(rowCount),
    members: z.object({
        moved: count,
        both: count,
        lineManagersReset: count,
    }),
    revoked: z.object({ refreshTokens: count, jobs: count }),
    blockers: z.array(z.object({ code: z.string(), message: z.string(), total: count })),
});
//# sourceMappingURL=org-restructure.js.map